import React, { useCallback, useEffect } from 'react';
import { useUI } from '../../contexts';

type SearchPanelProps = {
  onFindNext: () => void;
  onFindPrevious: () => void;
  onReplace: () => void;
  onReplaceAll: () => void;
};

export const SearchPanel = React.memo(function SearchPanel({
  onFindNext,
  onFindPrevious,
  onReplace,
  onReplaceAll,
}: SearchPanelProps) {
  const {
    searchPanelOpen,
    setSearchPanelOpen,
    searchQuery,
    setSearchQuery,
    replaceQuery,
    setReplaceQuery,
    searchOptions,
    setSearchOptions,
    searchMatches,
    currentMatchIndex,
    searchInputRef,
  } = useUI();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSearchPanelOpen(false);
      } else if (e.key === 'Enter') {
        if (e.shiftKey) {
          onFindPrevious();
        } else {
          onFindNext();
        }
      }
    },
    [onFindNext, onFindPrevious, setSearchPanelOpen]
  );

  const toggleOption = useCallback(
    (option: 'caseSensitive' | 'wholeWord' | 'regex') => {
      setSearchOptions({
        ...searchOptions,
        [option]: !searchOptions[option],
      });
    },
    [searchOptions, setSearchOptions]
  );

  useEffect(() => {
    if (searchPanelOpen && searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }
  }, [searchPanelOpen, searchInputRef]);

  if (!searchPanelOpen) return null;

  const matchText =
    searchMatches.length === 0
      ? searchQuery
        ? 'No results'
        : ''
      : `${currentMatchIndex + 1} of ${searchMatches.length}`;

  return (
    <div className="search-panel">
      <div className="search-row">
        <div className="search-input-group">
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <span className="search-count">{matchText}</span>
        </div>
        <div className="search-options">
          <button
            className={`search-option ${searchOptions.caseSensitive ? 'active' : ''}`}
            onClick={() => toggleOption('caseSensitive')}
            title="Match Case (Alt+C)"
          >
            Aa
          </button>
          <button
            className={`search-option ${searchOptions.wholeWord ? 'active' : ''}`}
            onClick={() => toggleOption('wholeWord')}
            title="Match Whole Word (Alt+W)"
          >
            [ab]
          </button>
          <button
            className={`search-option ${searchOptions.regex ? 'active' : ''}`}
            onClick={() => toggleOption('regex')}
            title="Use Regular Expression (Alt+R)"
          >
            .*
          </button>
        </div>
        <div className="search-actions">
          <button className="search-btn" onClick={onFindPrevious} title="Previous Match (Shift+Enter)">
            ↑
          </button>
          <button className="search-btn" onClick={onFindNext} title="Next Match (Enter)">
            ↓
          </button>
          <button className="search-btn close" onClick={() => setSearchPanelOpen(false)} title="Close (Escape)">
            ×
          </button>
        </div>
      </div>
      <div className="search-row replace-row">
        <div className="search-input-group">
          <input
            type="text"
            className="search-input"
            placeholder="Replace"
            value={replaceQuery}
            onChange={(e) => setReplaceQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="replace-actions">
          <button
            className="search-btn replace"
            onClick={onReplace}
            title="Replace (Cmd+Shift+1)"
            disabled={searchMatches.length === 0}
          >
            Replace
          </button>
          <button
            className="search-btn replace-all"
            onClick={onReplaceAll}
            title="Replace All (Cmd+Shift+Enter)"
            disabled={searchMatches.length === 0}
          >
            All
          </button>
        </div>
      </div>
    </div>
  );
});
